import { useCallback, useMemo, useRef } from "react";

/**
 * The set of form fields whose input box currently holds something that is
 * NOT what the form value says.
 *
 * Today one cell reports into it: the EMAIL editor (`TCCEmail`). It never hands
 * an unstorable address to the form — the form keeps the last storable value —
 * so a typo behind a stored address leaves the box showing one thing and the
 * form holding another. A Save/Create button that only looks at the form
 * would write the stale value while the operator watches a red error under
 * the text they typed. `onFieldInvalid` is the cell's channel; `has()` is the
 * button's question.
 *
 * A ref, not state: the button reads it inside its click handler, in the same
 * discrete event that just blurred the input, and needs the answer the cell
 * gave a moment ago — not the one from the last render.
 */
export function useInvalidFields() {
  const invalid = useRef<Set<string>>(new Set());
  const onFieldInvalid = useCallback((fieldName: string, isInvalid: boolean) => {
    if (isInvalid) invalid.current.add(fieldName);
    else invalid.current.delete(fieldName);
  }, []);
  const has = useCallback(() => invalid.current.size > 0, []);
  /** The field names currently reported invalid, for a host that wants to
   *  bring the culprit on screen. */
  const names = useCallback(() => Array.from(invalid.current), []);
  // One stable object: hosts put it in dependency arrays and cell contexts.
  return useMemo(() => ({ onFieldInvalid, has, names }), [onFieldInvalid, has, names]);
}
